import { useState } from 'react'
import { api } from '../api'
import { useAuth } from '../auth/AuthContext'
import { useToast } from '../components/Toasts'
import { ErrorState, LoadingRows } from '../components/States'
import { RequestPill } from '../components/StatusPill'
import { useAsync } from '../lib/useAsync'
import { FIELD_LABELS, diffLines } from '../lib/diff'
import { initials, plural } from '../lib/format'
import { isStale, staleMessage } from '../lib/changeRequests'
import type { ChangeRequest, DocSection, JobDescription } from '../types'
import { BackButton } from '../components/BackButton'
import { sectionChanges, sectionsOf } from '../lib/sections'
import type { DiffLine } from '../lib/diff'

export function ApprovalsTab() {
  const { isAdmin, refresh } = useAuth()
  const requests = useAsync(() => api.listChangeRequests(), [])
  const [selected, setSelected] = useState<string | null>(null)

  const list = requests.data ?? []
  const chosen = selected ? list.find((r) => r.id === selected) ?? null : null

  const afterDecision = () => {
    setSelected(null)
    requests.reload()
    void refresh()
  }

  if (requests.error) return <ErrorState message={requests.error} onRetry={requests.reload} />
  if (requests.loading) {
    return (
      <div className="card card--flush">
        <LoadingRows rows={3} columns={3} />
      </div>
    )
  }

  if (chosen) {
    return <DiffView request={chosen} onBack={() => setSelected(null)} onDecided={afterDecision} />
  }

  const pending = list.filter((r) => r.status === 'pending')

  if (isAdmin) {
    if (!pending.length) {
      return (
        <div className="state state--empty">
          <h2>Nothing waiting on you</h2>
          <p>
            When a manager proposes a change to a role, it lands here with the current text beside
            the proposed text.
          </p>
        </div>
      )
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {pending.map((r) => (
          <button type="button" className="req-card" key={r.id} onClick={() => setSelected(r.id)}>
            <span className="avatar" aria-hidden="true">{initials(r.requestedBy)}</span>
            <span className="req-card__body">
              <span className="req-card__title">
                <b>{r.jdTitle}</b>
                <span className="badge">{plural(Object.keys(r.changes).length, 'field')} changed</span>
              </span>
              <span className="req-card__meta">
                {r.requestedBy} · {r.requestedByRole} · {r.requestedAt}
              </span>
              <span className="req-card__reason">“{r.reason}”</span>
            </span>
            <span className="req-card__cta">Review the difference →</span>
          </button>
        ))}
      </div>
    )
  }

  // A manager sees only their own requests, and cannot decide any of them.
  if (!list.length) {
    return (
      <div className="state state--empty">
        <h2>You have not asked for any changes yet</h2>
        <p>
          Open a job description, choose “Edit and request approval”, and say why. It will show up
          here with HR’s answer.
        </p>
      </div>
    )
  }

  const ordered = [...list].sort(
    (a, b) => (a.status === 'pending' ? 0 : 1) - (b.status === 'pending' ? 0 : 1),
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {ordered.map((r) => (
        <div className="req-card" key={r.id}>
          <div className="req-card__body">
            <div className="req-card__title">
              <b>{r.jdTitle}</b>
              <RequestPill status={r.status} />
              <span className="hint">{plural(Object.keys(r.changes).length, 'field')} changed</span>
            </div>
            <div className="req-card__meta">Submitted {r.requestedAt}</div>
            <div className="req-card__reason">“{r.reason}”</div>
            {r.decisionNote && (
              <div className="note" style={{ marginTop: 2 }}>
                HR’s comment: {r.decisionNote}
              </div>
            )}
          </div>
          <button type="button" className="btn btn--sm" onClick={() => setSelected(r.id)}>
            View changes
          </button>
        </div>
      ))}
    </div>
  )
}

function DiffView({
  request,
  onBack,
  onDecided,
}: {
  request: ChangeRequest
  onBack: () => void
  onDecided: () => void
}) {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const role = useAsync(() => api.getJobDescription(request.jdId), [request.jdId])
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const jd = role.data
  // Checked as soon as the role loads, so the warning is on screen before
  // anyone reaches for Approve rather than after a click that fails.
  const stale = jd ? isStale(jd, request) : false

  const approve = async () => {
    setBusy(true)
    try {
      await api.approveChangeRequest(request.id)
      toast.ok('Approved. The version went up and the change is in the history.')
      onDecided()
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not go through.')
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    if (!note.trim()) {
      toast.fail('Add a comment so the requester knows why.')
      return
    }
    setBusy(true)
    try {
      await api.rejectChangeRequest(request.id, note)
      toast.ok('Declined, with your comment sent back.')
      onDecided()
    } catch (err) {
      toast.fail(err instanceof Error ? err.message : 'That did not go through.')
    } finally {
      setBusy(false)
    }
  }

  const backTo = isAdmin ? 'the queue' : 'my requests'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <BackButton to={backTo} onClick={onBack} />

      <div className="card card--pad" style={{ padding: 20 }}>
        <div className="title-row" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 600 }}>{request.jdTitle}</h2>
            <div className="req-card__meta">
              {isAdmin ? `Requested by ${request.requestedBy} · ` : 'Submitted '}
              {request.requestedAt}
              {jd && ` · currently v${jd.version}`}
            </div>
          </div>
          <RequestPill status={request.status} />
        </div>
        <div className="note" style={{ marginTop: 14 }}>
          “{request.reason}”
        </div>
      </div>

      {role.error && <ErrorState message={role.error} onRetry={role.reload} />}

      {jd && stale && (
        <div className="banner banner--warn" role="alert">
          <span aria-hidden="true">!</span>
          <span>
            <b>This request is out of date.</b> {staleMessage(jd, request)}
          </span>
        </div>
      )}

      {jd &&
        (Object.keys(request.changes) as (keyof typeof request.changes)[]).map((field) => {
          // The sections are compared section by section (FR-19), against
          // what the manager was shown: the role's own sections, or the ones
          // built from its fields.
          if (field === 'sections') {
            return (
              <SectionDiff
                key={field}
                current={sectionsOf(jd)}
                proposed={(request.changes.sections ?? []) as DocSection[]}
              />
            )
          }
          const before = jd[field as keyof JobDescription] as string | number | string[]
          const after = request.changes[field] as string | number | string[]
          const { oldLines, newLines } = diffLines(before ?? '', after)
          return (
            <div className="card card--flush" key={field}>
              <h3 className="card__head">{FIELD_LABELS[field] ?? field}</h3>
              <Sides oldLines={oldLines} newLines={newLines} />
            </div>
          )
        })}

      {isAdmin && request.status === 'pending' && jd && (
        <div className="card card--pad" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="field">
            <span>Comment, required to decline</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Say what would need to change for this to be approved"
            />
          </label>
          <div className="actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void approve()}
              disabled={busy || stale}
              title={stale ? 'This request was written against an older version of the role' : undefined}
            >
              {busy ? 'Working…' : `Approve and publish v${jd.version + 1}`}
            </button>
            {/* Declining stays available: a stale request still needs an answer. */}
            <button type="button" className="btn" onClick={() => void reject()} disabled={busy}>
              Decline
            </button>
            <span className="hint">
              {stale
                ? 'You can still decline this and ask for it to be redone.'
                : `Approving takes this role from v${jd.version} to v${jd.version + 1} and writes to the history.`}
            </span>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="note">
          HR reviews and publishes this change. You will see the outcome here.
        </div>
      )}
    </div>
  )
}

/** Current beside proposed, with what was taken out and what was put in marked. */
function Sides({ oldLines, newLines }: { oldLines: DiffLine[]; newLines: DiffLine[] }) {
  return (
    <div className="diff-grid">
      <div className="diff-side">
        <h3>Current</h3>
        <div className="diff-lines">
          {oldLines.length ? oldLines.map((l, i) => (
            <div className={`diff-line${l.changed ? ' diff-line--removed' : ''}`} key={i}>
              {l.changed && <span className="sr-only">Removed: </span>}
              {l.text || '—'}
            </div>
          )) : <div className="diff-line">—</div>}
        </div>
      </div>
      <div className="diff-side">
        <h3>Proposed</h3>
        <div className="diff-lines">
          {newLines.length ? newLines.map((l, i) => (
            <div className={`diff-line${l.changed ? ' diff-line--added' : ''}`} key={i}>
              {l.changed && <span className="sr-only">Added: </span>}
              {l.text || '—'}
            </div>
          )) : <div className="diff-line">—</div>}
        </div>
      </div>
    </div>
  )
}

/**
 * The sections a manager changed, one card each (FR-19): a section added,
 * removed or renamed, the lines changed under a heading, or a new order.
 * Sections nobody touched are not shown, so HR reads only what moved.
 */
function SectionDiff({ current, proposed }: { current: DocSection[]; proposed: DocSection[] }) {
  // A document heading keeps its own case. The card's label is upper-cased
  // like every other label, and upper-casing a heading would be rewording it.
  const named = (heading: string) => (
    <span className="card__head-name">{heading ? `“${heading}”` : 'the section with no heading'}</span>
  )
  const all = (lines: string[], changed: boolean): DiffLine[] => lines.map((text) => ({ text, changed }))
  const changes = sectionChanges(current, proposed)
  if (!changes.length) return null
  return (
    <>
      {changes.map((c, i) => {
        switch (c.kind) {
          case 'added':
            return (
              <div className="card card--flush" key={i}>
                <h3 className="card__head">Section added: {named(c.heading)}</h3>
                <Sides oldLines={[]} newLines={all(c.lines, true)} />
              </div>
            )
          case 'removed':
            return (
              <div className="card card--flush" key={i}>
                <h3 className="card__head">Section removed: {named(c.heading)}</h3>
                <Sides oldLines={all(c.lines, true)} newLines={[]} />
              </div>
            )
          case 'renamed': {
            const { oldLines, newLines } = diffLines(c.before, c.after)
            return (
              <div className="card card--flush" key={i}>
                <h3 className="card__head">Heading renamed: {named(c.from)} to {named(c.to)}</h3>
                <Sides
                  oldLines={[{ text: c.from || '(no heading)', changed: true }, ...oldLines]}
                  newLines={[{ text: c.to || '(no heading)', changed: true }, ...newLines]}
                />
              </div>
            )
          }
          case 'lines': {
            const { oldLines, newLines } = diffLines(c.before, c.after)
            return (
              <div className="card card--flush" key={i}>
                <h3 className="card__head">Lines changed under {named(c.heading)}</h3>
                <Sides oldLines={oldLines} newLines={newLines} />
              </div>
            )
          }
          case 'order':
            return (
              <div className="card card--flush" key={i}>
                <h3 className="card__head">Sections in a new order</h3>
                <Sides
                  oldLines={c.before.map((h, j) => ({ text: h || '(no heading)', changed: h !== c.after[j] }))}
                  newLines={c.after.map((h, j) => ({ text: h || '(no heading)', changed: h !== c.before[j] }))}
                />
              </div>
            )
        }
      })}
    </>
  )
}
